import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Formik, Form, FieldArray } from 'formik';
import classNames from 'classnames/bind';

import Modal from './Modal';
import TextField from '../Form/TextField';
import Button from '../Button';
import { ChatContext, events } from './ChatContext';
import { getActiveRoom } from '~/modules/chat';

import styles from './PollModal.scss';

interface PollModalProps {
  close: () => void;
  roomId: string;
}

interface PollFormValues {
  title: string;
  options: string[];
  duration: number | null; // in seconds, null for no limit
}

const cx = classNames.bind(styles);

const DURATION_OPTIONS = [
  { label: 'No limit', value: 0 },
  { label: '1 minute', value: 60 },
  { label: '5 minutes', value: 300 },
  { label: '10 minutes', value: 600 },
  { label: '30 minutes', value: 1800 },
];

function PollModal(props: PollModalProps) {
  const intl = useIntl();
  const { client } = useContext(ChatContext);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [pendingShowForm, setPendingShowForm] = useState(false);

  const currentRoomId = useSelector(getActiveRoom);
  const activeRoom = useSelector(state => state.entities.chatRooms[currentRoomId]);
  const activePoll = activeRoom?.poll && !activeRoom.poll.finished;

  const initialValues = {
    title: '',
    options: ['', ''],
    duration: null, // default indefinite
  };

  const validate = (values: PollFormValues) => {
    const errors: Partial<PollFormValues & { options?: string[] }> = {};
    if (!values.title.trim()) {
      errors.title = intl.formatMessage({ id: 'PollModal_Validation_Title', defaultMessage: 'Poll title is required.' });
    }

    if (values.title.length > 100) {
      errors.title = intl.formatMessage({ id: 'PollModal_Validation_TitleLength', defaultMessage: 'Poll title must be 100 characters or less.' });
    }

    if (!values.options || values.options.length < 2) {
      errors.options = [intl.formatMessage({ id: 'PollModal_Validation_Options', defaultMessage: 'At least two options are required.' })];
    } else {
      const optionErrors: string[] = [];
      const seen = new Set<string>();
      values.options.forEach((opt, idx) => {
        const normalized = opt.trim().toLowerCase();
        if (!opt.trim()) {
          optionErrors[idx] = intl.formatMessage({ id: 'PollModal_Validation_OptionRequired', defaultMessage: 'Option is required.' });
        } else if (seen.has(normalized)) {
          optionErrors[idx] = intl.formatMessage({ id: 'PollModal_Validation_OptionDuplicate', defaultMessage: 'Duplicate option.' });
        } else {
          seen.add(normalized);
        }
      });
      if (optionErrors.length > 0) {
        errors.options = optionErrors;
      }
    }
    return errors;
  }

  const handleSubmit = (values) => {
    props.close();
    client?.emit(events.START_POLL, {
      title: values.title,
      options: values.options,
      roomId: props.roomId,
      duration: values.duration || null,
    });
  };

  useEffect(() => {
    // If a poll is already running, show confirmation before showing the form
    if (activePoll && !pendingShowForm) {
      setShowReplaceConfirm(true);
    }
  }, []);

  // If a poll is already running, show confirmation before showing the form
  if ((showReplaceConfirm || activePoll) && !pendingShowForm) {
    return (
      <Modal title={intl.formatMessage({ id: 'PollModal_ReplaceTitle', defaultMessage: 'Replace current poll?' })} close={props.close}>
        <div style={{ textAlign: 'center' }}>
          {intl.formatMessage({ id: 'PollModal_ReplaceText', defaultMessage: 'A poll is already running. Starting a new one will replace the current poll and remove all votes. Continue?' })}
          <br /><br />
          <button onClick={props.close} style={{ marginLeft: 8 }}>
            {intl.formatMessage({ id: 'PollModal_ReplaceCancel', defaultMessage: 'Cancel' })}
          </button>
          <button onClick={() => { setShowReplaceConfirm(false); setPendingShowForm(true); }}>
            {intl.formatMessage({ id: 'PollModal_ReplaceConfirm', defaultMessage: 'Replace poll' })}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={intl.formatMessage({ id: 'PollModal_Title', defaultMessage: 'Create poll' })} close={props.close}>
      <Formik 
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, isSubmitting, setFieldValue }) => (
          <Form className={cx('PollModal')}>
            <TextField
              fullWidth
              name="title"
              onChange={handleChange}
              maxLength={100}
              error={touched.title && typeof errors.title === 'string' ? errors.title : undefined}
            >
              {intl.formatMessage({ id: 'PollModal_TitleLabel', defaultMessage: 'Poll title' })}
            </TextField>

            <FieldArray name="options">
              {({ push, remove }) => (
                <div className={cx('PollModal__Options')}>
                  {values.options.map((option, idx) => (
                    <div key={idx} className={cx('PollModal__OptionRow')}>
                      <TextField
                        size="small"
                        fullWidth
                        maxLength={50}
                        name={`options.${idx}`}
                        onChange={handleChange}
                        error={touched.options && errors.options && touched.options[idx] && typeof errors.options[idx] === 'string' ? errors.options[idx] : undefined}
                        placeholder={intl.formatMessage({ id: 'PollModal_OptionLabel', defaultMessage: 'Option {num}' }, { num: idx + 1 })}
                      />
                      {values.options.length > 2 && (
                        <Button
                          color="transparent"
                          onClick={() => remove(idx)}
                          className={cx('PollModal__RemoveButton')}
                          title={intl.formatMessage({ id: 'PollModal_RemoveOption', defaultMessage: 'Remove' })}
                        >
                          <span role="img" className="ion-minus-circled" aria-label="remove" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button color="transparent" onClick={() => push('')} className={cx('PollModal__AddOption')}>
                    <span role="img" aria-label="plus" className="ion-plus" />
                    &nbsp;
                    {intl.formatMessage({ id: 'PollModal_AddOption', defaultMessage: 'Add option' })}
                  </Button>
                </div>
              )}
            </FieldArray>
            <div className={cx('PollModal__Duration')} style={{ marginBottom: 16 }}>
              <label htmlFor="duration">
                {intl.formatMessage({ id: 'PollModal_DurationLabel', defaultMessage: 'Poll duration (optional)' })}
              </label>
              <select
                id="duration"
                name="duration"
                value={values.duration}
                onChange={e => setFieldValue('duration', Number(e.target.value))}
                style={{ marginLeft: 8 }}
              >
                {DURATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className={cx('PollModal__Footer')}>
              <button className={cx('PollModal__SubmitButton')} disabled={isSubmitting} type="submit">
                {intl.formatMessage({ id: 'PollModal_Create', defaultMessage: 'Create poll' })}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default PollModal;
